import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { CalcProject, CalcSheet } from "../../types/calc";
import { calcSheetPath } from "./calcSheetState";
import { prettySize } from "./calcDerive";
import { useCalcGrouping } from "./calcGrouping";
import { LOAD_CALC_TEMPLATE_BY_ID, loadTemplateIdFromCalculatorId } from "./loadCalcDefinitions";

import "../../styles/calc-shell.css";

export type CalcProjectNavSection = "report" | "viewer" | "add" | "none";

type CalcProjectNavProps = {
  project?: CalcProject | null;
  sheets?: CalcSheet[];
  activeNav?: CalcProjectNavSection;
  activeSheetId?: string | null;
  onNavigate?: () => void;
};

function navUtilization(sheet: CalcSheet): { className: string; label: string } {
  if (loadTemplateIdFromCalculatorId(sheet.calculatorId)) {
    if (sheet.status === "blocked") return { className: "is-fail", label: "blocked" };
    if (sheet.status === "partial") return { className: "is-near", label: "partially calculated" };
    if (sheet.status === "computed") return { className: "is-pass", label: "calculated" };
    if (sheet.status === "not_applicable") return { className: "is-pass", label: "not applicable" };
    return { className: "is-idle", label: "not calculated" };
  }
  if (sheet.passes === false || (sheet.maxDcr != null && sheet.maxDcr > 1)) {
    return { className: "is-fail", label: "failing" };
  }
  if (sheet.maxDcr != null && sheet.maxDcr >= 0.9) {
    return { className: "is-near", label: "near limit" };
  }
  if (sheet.passes === true) return { className: "is-pass", label: "passing" };
  return { className: "is-idle", label: "not calculated" };
}

export function CalcProjectNav({
  project,
  sheets = [],
  activeNav = "none",
  activeSheetId = null,
  onNavigate,
}: CalcProjectNavProps) {
  const projectId = project?.id;
  const projectPath = projectId ? `/app/calculations/${projectId}` : "/app/calculations";
  const grouping = useCalcGrouping(projectId ?? "", sheets);
  const groups = grouping.groups.filter((group) => group.members.length > 0);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  return (
    <div className="cs-project-nav">
      <div className="cs-sidebar-head">
        <div className="cs-sidebar-name">{project?.name ?? "Calc project"}</div>
        <div className="cs-sidebar-addr">{project?.address || "No address"}</div>
      </div>

      <div className="cs-sidebar-scroll">
        <nav className="cs-project-sections" aria-label="Project sections">
          <Link
            className={`cs-nav-item${activeNav === "report" ? " is-active" : ""}`}
            to={projectId ? `${projectPath}/report-details` : projectPath}
            onClick={onNavigate}
          >
            <span className="cs-nav-label">Report details</span>
          </Link>
          <Link
            className={`cs-nav-item${activeNav === "viewer" ? " is-active" : ""}`}
            to={projectPath}
            onClick={onNavigate}
          >
            <span className="cs-nav-label">Overview</span>
          </Link>
          <Link
            className={`cs-nav-item${activeNav === "add" ? " is-active" : ""}`}
            to={projectId ? `${projectPath}/add` : projectPath}
            onClick={onNavigate}
          >
            <span className="cs-nav-label">New calculation</span>
          </Link>
        </nav>

        <div className="cs-groups-label">
          <span>
            Calculations
            <small className="cs-groups-mode">
              {grouping.preferences.mode === "automatic" ? "automatic" : "manual"}
            </small>
          </span>
          <span className="cs-groups-count num">{sheets.length}</span>
        </div>
        {groups.length === 0 ? (
          <div className="cs-sidebar-empty">No calculations yet</div>
        ) : (
          groups.map((group) => {
            const isOpen = collapsed[group.id] !== true;
            return (
              <section className="cs-group-section" key={group.id}>
                <button
                  type="button"
                  className="cs-group-head"
                  aria-expanded={isOpen}
                  onClick={() => setCollapsed((current) => ({ ...current, [group.id]: isOpen }))}
                >
                  <span className={`cs-group-caret${isOpen ? " is-open" : ""}`} aria-hidden="true">›</span>
                  <span className="cs-group-name">{group.label}</span>
                  <span className="cs-group-count num">{group.members.length}</span>
                </button>
                {isOpen && (
                  <div className="cs-group-members">
                    {group.members.map((sheet) => {
                      const isActive = sheet.id === activeSheetId;
                      const utilization = navUtilization(sheet);
                      const loadTemplateId = loadTemplateIdFromCalculatorId(sheet.calculatorId);
                      const size = (sheet.callout && prettySize(sheet.callout))
                        || (loadTemplateId ? LOAD_CALC_TEMPLATE_BY_ID[loadTemplateId].shortName : null)
                        || "—";
                      return (
                        <Link
                          key={sheet.id}
                          className={`cs-member${isActive ? " is-active" : ""}`}
                          to={projectId ? calcSheetPath(projectId, sheet) : projectPath}
                          onClick={onNavigate}
                          aria-current={isActive ? "page" : undefined}
                          aria-label={`${sheet.name || "Untitled"}, ${size}, ${utilization.label}`}
                        >
                          <span className="cs-member-mark num">{sheet.name || "Untitled"}</span>
                          <span className={`cs-member-size num ${utilization.className}`}>{size}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

export function CalcProjectNavTrigger({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="cpn-trigger"
      onClick={onClick}
      aria-label="Open project calculations"
      title="Project calculations"
    >
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
        <path d="M6 2.5v11M8.5 5h2.5M8.5 8h2.5M8.5 11h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      <span>Project calculations</span>
    </button>
  );
}

export function CalcProjectNavDrawer({
  open,
  onClose,
  ...navProps
}: CalcProjectNavProps & { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="cpn-overlay" role="dialog" aria-modal="true" aria-label="Project calculations">
      <div className="cpn-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="cpn-panel">
        <div className="cpn-head">
          <div>
            <div className="cpn-title">Project calculations</div>
          </div>
          <button type="button" className="cpn-close" onClick={onClose} aria-label="Close project calculations">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <CalcProjectNav {...navProps} onNavigate={onClose} />
      </aside>
    </div>,
    document.body,
  );
}
